import { useEffect, useRef, useState, useCallback } from "react";

/**
 * Simple browser recorder using MediaRecorder. Returns state helpers and
 * the final Blob when stopped. The returned blob is tagged with the ACTUAL
 * mimeType the browser chose (iOS Safari cannot record webm, so it uses
 * audio/mp4; Chrome/Firefox/Android happily produce audio/webm).
 */

// Preference order for MediaRecorder mime types. Any format Whisper accepts.
// iOS Safari only supports audio/mp4 (AAC) — it MUST be in the list so the
// picker doesn't fall through to no-mime and pick something unexpected.
const MIME_CANDIDATES = [
  "audio/webm;codecs=opus",
  "audio/webm",
  "audio/mp4;codecs=mp4a.40.2",
  "audio/mp4",
  "audio/aac",
  "audio/wav",
];

// Map a mime type to an extension Whisper can identify from the filename.
export function extForMime(mime) {
  const m = (mime || "").toLowerCase();
  if (m.includes("webm")) return "webm";
  if (m.includes("mp4") || m.includes("aac") || m.includes("mp4a") || m.includes("m4a")) return "m4a";
  if (m.includes("wav")) return "wav";
  if (m.includes("mpeg") || m.includes("mp3")) return "mp3";
  if (m.includes("ogg")) return "ogg";
  return "webm";
}

export function useRecorder() {
  const streamRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);

  const [recording, setRecording] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [blob, setBlob] = useState(null);
  const [error, setError] = useState(null);

  const cleanup = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    recorderRef.current = null;
  }, []);

  useEffect(() => () => cleanup(), [cleanup]);

  const start = useCallback(async () => {
    setError(null);
    setBlob(null);
    setSeconds(0);
    try {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error("Este navegador não suporta gravação de áudio. Tente atualizar o Safari ou Chrome.");
      }
      if (typeof window.MediaRecorder === "undefined") {
        // iOS < 14.3 lacks MediaRecorder — the app can't record on such devices.
        throw new Error("Seu navegador não suporta gravação. Atualize para iOS 14.3+ ou use Chrome / Safari mais recentes.");
      }
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      // Pick the first supported mime; iOS Safari will land on audio/mp4.
      let mime = "";
      for (const cand of MIME_CANDIDATES) {
        try {
          if (window.MediaRecorder.isTypeSupported && window.MediaRecorder.isTypeSupported(cand)) {
            mime = cand;
            break;
          }
        } catch { /* isTypeSupported throws on some old browsers */ }
      }

      const rec = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
      // rec.mimeType is set once the recorder starts; use it as the source
      // of truth (some Safari builds report a different type than we asked
      // for, e.g. "audio/mp4;codecs=mp4a.40.2").
      chunksRef.current = [];
      rec.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
      };
      rec.onstop = () => {
        // Prefer the recorder's reported mime; fall back to what we asked for.
        const actualType = rec.mimeType || mime || "audio/webm";
        const b = new Blob(chunksRef.current, { type: actualType });
        setBlob(b);
        cleanup();
      };
      rec.onerror = (ev) => {
        setError(ev?.error?.message || "Ocorreu um erro durante a gravação.");
        cleanup();
        setRecording(false);
      };
      rec.start(200);
      recorderRef.current = rec;
      setRecording(true);
      timerRef.current = setInterval(() => setSeconds((s) => s + 1), 1000);
    } catch (e) {
      const name = e?.name || "";
      let msg = e?.message || "Não foi possível acessar o microfone.";
      if (name === "NotAllowedError" || name === "PermissionDeniedError") {
        msg = "Acesso ao microfone negado. Vá em Ajustes → Safari → Câmera e microfone (ou Chrome → Site settings) e permita o microfone.";
      } else if (name === "NotFoundError" || name === "DevicesNotFoundError") {
        msg = "Nenhum microfone encontrado. Verifique se o dispositivo tem microfone e se ele não está em uso por outro app.";
      } else if (name === "NotReadableError") {
        msg = "O microfone está sendo usado por outro aplicativo. Feche outras chamadas ou apps de gravação e tente novamente.";
      }
      setError(msg);
      setRecording(false);
      cleanup();
    }
  }, [cleanup]);

  const stop = useCallback(() => {
    if (recorderRef.current && recorderRef.current.state !== "inactive") {
      recorderRef.current.stop();
    }
    setRecording(false);
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const reset = useCallback(() => {
    setBlob(null);
    setSeconds(0);
    setError(null);
  }, []);

  return { recording, seconds, blob, error, start, stop, reset };
}

export function formatTime(s) {
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${String(m).padStart(2, "0")}:${String(r).padStart(2, "0")}`;
}
